import DatePicker from 'react-datepicker';
import { Images } from '@business/assets/images';
import { yupResolver } from '@hookform/resolvers/yup';
import { editProfileSchema } from '@utils/validations/my-profile';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { routePaths } from '@utils/constants/routePaths';
import { SVGAssets } from '@business/assets/vectors';
import {
  useAddUserDetailsMutation,
  useUserDetailsByUserIdQuery
} from '@ports/internal-services/userDetails/userDetailsSlice';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import { AlertProps } from '@business/interfaces/alert';
import {
  getAlertState,
  resetAlertState,
  setAlertDetails
} from '@ports/common/AlertStateSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from '../Button';
import { Input } from '../Input';
import Breadcrumb from '../BreadCrumbs/BreadCrumbs';
import { Alert } from '../Alert';
import { Avatar } from '../Avatar';
import { UploadOptions } from './UploadOptions';

interface MyProfileFormValues {
  firstName: string;
  lastName: string;
  profile: string;
  dateOfBirth: string;
  email: string;
  contactNumber: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: any;
}

interface MyProfileFormProps {
  isEdit: boolean;
}

const defaultValues: MyProfileFormValues = {
  firstName: '',
  lastName: '',
  profile: 'Owner',
  dateOfBirth: '',
  email: '',
  contactNumber: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  country: ''
};

export const MyProfileForm: FC<MyProfileFormProps> = ({ isEdit }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const alertShowData = useSelector(getAlertState);
  const optionsRef = useRef<HTMLDivElement>(null);
  const profileCircleRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertData, setAlertData] = useState<AlertProps | null>(null);
  const options = useMemo(() => countryList().getData(), []);

  const [UpdateUserDetails] = useAddUserDetailsMutation();
  const { data: userDetails, refetch } = useUserDetailsByUserIdQuery({} as any);
  const userId = userDetails?.data?.userId;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        optionsRef.current &&
        !optionsRef.current.contains(event.target as Node) &&
        profileCircleRef.current &&
        !profileCircleRef.current.contains(event.target as Node) &&
        !showUploadModal
      ) {
        setShowOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUploadModal]);

  const handleMouseEnter = () => {
    if (isEdit) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      setShowOptions(true);
    }
  };

  const handleMouseLeave = () => {
    if (isEdit && !showUploadModal) {
      hoverTimeoutRef.current = setTimeout(() => {
        setShowOptions(false);
      }, 300);
    }
  };

  // Keep options visible when modal is open
  useEffect(() => {
    if (showUploadModal) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    }
  }, [showUploadModal]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setFocus
  } = useForm<MyProfileFormValues>({
    resolver: yupResolver(editProfileSchema),
    defaultValues: defaultValues,
    mode: 'onChange',
    shouldFocusError: true
  } as any);

  const hasErrors = Object.keys(errors).length > 0;

  useEffect(() => {
    if (userDetails?.data) {
      reset({
        firstName: userDetails?.data?.firstName || '',
        lastName: userDetails?.data?.lastName || '',
        dateOfBirth: userDetails?.data?.dateOfBirth || '',
        email: userDetails?.data?.email || '',
        contactNumber: userDetails?.data?.contactNumber || '',
        address: userDetails?.data?.line1 || '',
        city: userDetails?.data?.city || '',
        state: userDetails?.data?.state || '',
        zipCode: userDetails?.data?.postal_code || '',
        country: userDetails?.data?.country || '',
        profile: 'Owner',
        profileImage: userDetails?.data?.profileImage || ''
      });
    }
  }, [userDetails?.data]);

  useEffect(() => {
    refetch();
  }, []);

  const onSubmit = (data: MyProfileFormValues) => {
    const dataObj = {
      createUserDetails: {
        firstName: data?.firstName,
        lastName: data?.lastName,
        dateOfBirth: data?.dateOfBirth,
        email: data?.email,
        line1: data?.address,
        city: data?.city,
        state: data?.state,
        postal_code: data?.zipCode,
        country: data?.country,
        contactNumber: data?.contactNumber,
        profile: 'owner'
      }
    };
    UpdateUserDetails(dataObj)
      .then((res: any) => {
        if (res?.data) {
          dispatch(
            setAlertDetails({
              alert: {
                type: 'success',
                message: t('applySuccess')
              },
              scope: 'profile'
            })
          );

          navigate(routePaths.MyProfile);
        } else {
          setShowAlert(true);
          setAlertData({
            type: 'error',
            message: res?.error
          });
        }
      })
      .catch(err => {
        console.error('Error sending forgot password email:', err);
        setShowAlert(true);
        setAlertData({
          type: 'error',
          message: err.message
        });
      });
  };

  // Add effect to handle focus on validation errors
  useEffect(() => {
    const firstError = Object.keys(errors)[0] as keyof MyProfileFormValues;
    if (firstError) {
      setFocus(firstError);
    }
  }, [errors, setFocus]);

  const handleCancel = () => {
    navigate(routePaths.MyProfile);
  };

  useEffect(() => {
    if (alertShowData?.scope === 'profile' && alertShowData.alert) {
      setShowAlert(true);
      setAlertData(alertShowData.alert);
    }
  }, [alertShowData]);

  console.log();

  return (
    <div
      className={`overflow-hidden mx-auto ${isEdit ? 'max-w-6xl mt-6' : 'w-full'}`}>
      <div className="mb-2">
        <Breadcrumb
          paths={
            isEdit
              ? [
                  { label: t('myProfile'), href: routePaths.MyProfile },
                  { label: t('edit') }
                ]
              : [{ label: t('myProfile') }]
          }
        />
      </div>

      <div
        className={`${!isEdit ? 'flex justify-between items-center gap-3 mb-2' : 'mb-2'}`}>
        <div className="flex">
          <h1 className="font-bold">
            {isEdit ? t('editProfile') : t('myProfile')}
          </h1>

          {!isEdit && (
            <Button btnStyle="link" href={routePaths.EditProfile}>
              <span className="flex items-center gap-1 justify-center mt-1">
                <SVGAssets.PencilIcon className="w-5 h-5 ml-3" />
                <span className="text-[#7F9D48] text-sm mt-1">
                  {t('editProfile')}
                </span>
              </span>
            </Button>
          )}
        </div>

        {showAlert && (
          <div>
            <Alert
              onClose={() => {
                dispatch(resetAlertState());
                setShowAlert(false);
              }}
              {...alertData}
              autoHide={true}
            />
          </div>
        )}
      </div>
      <div className="relative h-46">
        <img
          src={Images.ProfileHeader}
          alt="profile-header"
          className="h-46 w-full md:object-contain"
        />
        <div className="absolute left-10 -bottom-1/3">
          <div className="relative w-40 h-40">
            <div
              ref={profileCircleRef}
              className={`bg-[#E9F6E6] w-full h-full rounded-full flex items-center justify-center 
    text-[#7F9D48] text-3xl font-semibold shadow-md border-[3px] border-solid 
    transition-all duration-300 relative overflow-hidden aspect-square
    ${showOptions ? 'border-white shadow-lg cursor-pointer' : 'border-transparent'}`}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}>
              <Avatar
                alt={`${userDetails?.data?.firstName}`}
                className="w-full h-full rounded-full"
                imgClassName="object-center"
                src={userDetails?.data?.profileImage}
              />

              {showOptions && (
                <div className="absolute inset-0 bg-[#00000050] flex items-center justify-center rounded-full">
                  <SVGAssets.TakePicIcon className="w-6 h-6 text-white" />
                </div>
              )}
            </div>

            {showOptions && (
              <div
                ref={optionsRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}>
                <UploadOptions
                  {...{ showUploadModal, setShowUploadModal }}
                  userId={userId}
                  onUploadSuccess={async () => {
                    await refetch();
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className={`p-5 pt-16 ${isEdit ? '' : 'pointer-events-none mt-6'}`}>
        {isEdit && (
          <div className="mb-10 mt-10">
            <div className="justify-end items-center flex gap-3">
              <Button
                btnStyle="link"
                type="button"
                onClick={handleCancel}
                className="px-6 py-3 !bg-[#F9FEE9] text-[var(--na-primary-color)] font-medium rounded-md hover:bg-[#F9FEE9] text-sm">
                {t('cancel')}
              </Button>
              <Button
                btnStyle="primary"
                type="submit"
                disabled={!isDirty}
                className={`px-6 rounded-lg py-3 text-sm ${
                  !isDirty ? 'opacity-50 cursor-not-allowed' : ''
                }`}>
                {t('saveChanges')}
              </Button>
            </div>
            {hasErrors && (
              <p className="text-red-600 text-xs font-medium justify-end flex mt-3">
                {t('pleaseFixErrors')}
              </p>
            )}
          </div>
        )}

        {/* Basic Info Section */}
        <div className="grid grid-cols-12 mb-8">
          {isEdit && (
            <div className="col-span-12 md:col-span-4">
              <h2 className="text-sm font-semibold text-[#505050] mb-6">
                {t('basic')}
              </h2>
            </div>
          )}
          <div className="col-span-12 md:col-span-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Input
                name="firstName"
                label={t('firstName')}
                control={control}
                type="text"
                isWhiteTheme={true}
                required
                error={errors.firstName?.message}
              />
              <Input
                name="lastName"
                label={t('lastName')}
                control={control}
                type="text"
                isWhiteTheme={true}
                required
                error={errors.lastName?.message}
              />

              <div className="md:col-span-2">
                <Input
                  name="profile"
                  label={t('profile')}
                  control={control}
                  type="text"
                  isWhiteTheme={true}
                  disabled={true}
                  error={errors?.profile?.message}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-[#353535] mb-2">
                  {t('dateOfBirth')}
                </label>

                <Controller
                  name="dateOfBirth"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <div className="relative w-full">
                      <DatePicker
                        selected={field.value ? new Date(field.value) : null}
                        onChange={(date: Date | null) =>
                          // Note: toISOString() uses UTC. Ensure this matches your backend requirements.
                          field.onChange(
                            date ? date.toISOString().split('T')[0] : ''
                          )
                        }
                        onBlur={field.onBlur}
                        maxDate={new Date()} // This prevents selecting future dates
                        dateFormat="yyyy-MM-dd"
                        placeholderText={t('dateOfBirth')}
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        yearDropdownItemNumber={100}
                        scrollableYearDropdown
                        wrapperClassName="w-full"
                        className="w-full inputWrapper-white rounded-xl px-4 py-3 pr-10 text-gray-800"
                      />
                    </div>
                  )}
                />

                <span className="text-red-500 text-xs">
                  {errors.dateOfBirth?.message}
                </span>
              </div>

              <div className="md:col-span-2">
                <Input
                  name="email"
                  label={t('email')}
                  disabled={true}
                  control={control}
                  type="email"
                  required
                  isWhiteTheme={true}
                  error={errors.email?.message}
                />
              </div>
            </div>
          </div>
        </div>

        <hr className="my-6" />

        {/* Contact Info */}
        <div className="grid grid-cols-12">
          {isEdit && (
            <div className="col-span-12 md:col-span-4">
              <h2 className="text-lg font-semibold text-[#505050] mb-6">
                {t('contact')}
              </h2>
            </div>
          )}
          <div className="col-span-12 md:col-span-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-[#353535] mb-1">
                  {t('contactNumber')}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <Controller
                  name="contactNumber"
                  control={control}
                  render={({ field }) => (
                    <PhoneInput
                      {...field}
                      international
                      defaultCountry="US"
                      limitMaxLength={true}
                      placeholder={t('contactNumberPlaceholder')}
                      className="phone-input-wrapper w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 outline-none transition text-base"
                    />
                  )}
                />
                <span className="text-red-500 text-xs">
                  {errors.contactNumber?.message}
                </span>
              </div>

              <Input
                name="address"
                label={t('address')}
                control={control}
                type="text"
                isWhiteTheme={true}
                required
                error={errors.address?.message}
              />
              <Input
                name="city"
                label={t('city')}
                control={control}
                type="text"
                isWhiteTheme={true}
                required
                error={errors.city?.message}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:col-span-2">
                <Input
                  name="state"
                  label={t('state')}
                  control={control}
                  type="text"
                  isWhiteTheme={true}
                  required
                  error={errors.state?.message}
                />
                <Input
                  name="zipCode"
                  label={t('zipCode')}
                  control={control}
                  type="text"
                  isWhiteTheme={true}
                  required
                  error={errors.zipCode?.message}
                />
              </div>

              {/* Country Dropdown */}
              <div className="md:col-span-2">
                <label
                  htmlFor="country"
                  className="block text-sm font-medium text-[#353535] mb-1">
                  {t('country')}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <Controller
                  name="country"
                  control={control}
                  rules={{ required: t('countryRequired') }}
                  render={({ field }) => (
                    <>
                      <Select
                        options={options}
                        placeholder="Select country"
                        classNamePrefix="billing-country-select"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                        value={
                          options.find(opt => opt.value === field.value) || null
                        }
                        onChange={val => field.onChange(val?.value)}
                        onBlur={field.onBlur}
                      />
                      <span className="text-red-500 text-xs">
                        {errors.country?.message}
                      </span>
                    </>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
